import { useCallback, useEffect, useRef, useState } from "react";
import { analyzeMorphology, refineMorphology, peekMorphology, fetchMorphologyConfig, verifyMorphologyToken } from "../api";
import { track } from "../utils/analytics";

/**
 * useIrabAnalysis — the Iʿrāb tool's full analyze→refine→peek-poll pipeline
 * as a reusable hook, shared by IrabPanel (overlay + public page) and the
 * PDF reader's Iʿrāb mode.
 *
 * Behavior (lifted verbatim from IrabPanel):
 * - analyze(text): instant cache/local pass; fires the metered AI refine
 *   when the server says it's allowed; free-tier quota 429 → upsell "quota";
 *   refine-locked 403 → upsell "refine_locked"; 402 keeps partial results.
 * - While refining, polls the cache-only peek endpoint every 4s and merges
 *   words progressively (spinners clear per word).
 * - peek(text): cache-read-only lookup — shows a sentence that has already
 *   been analyzed (e.g. an ayah an admin ran) without triggering the engine,
 *   the AI, or any quota. Nothing cached → tokens stay null.
 * All limits are server-enforced; this hook just mirrors the responses.
 */
export default function useIrabAnalysis({ context = "overlay" } = {}) {
  const [sentence, setSentence] = useState(""); // NFC text echoed by the backend
  const [tokens, setTokens] = useState(null);
  const [refining, setRefining] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [maxChars, setMaxChars] = useState(200);
  const [tier, setTier] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [refineRemaining, setRefineRemaining] = useState(null);
  const [upsell, setUpsell] = useState(null); // "refine_locked" | "quota" | null
  const [upsellRetryAt, setUpsellRetryAt] = useState(null);
  const [subscriberDaily, setSubscriberDaily] = useState(null);
  const [isStaff, setIsStaff] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchMorphologyConfig()
      .then((cfg) => {
        if (cfg.max_chars) setMaxChars(cfg.max_chars);
        setTier(cfg.tier || null);
        setRemaining(cfg.sentences_remaining_today);
        setRefineRemaining(cfg.refine_remaining_today);
        setSubscriberDaily(cfg.subscriber_daily_sentences ?? null);
        setIsStaff(!!cfg.is_staff);
      })
      .catch(() => {}); // config is best-effort; the server enforces limits
    return () => clearInterval(pollRef.current); // never leak the peek poll
  }, []);

  const mergeTokens = useCallback((updates) => {
    setTokens((prev) => ({ ...(prev || {}), ...(updates || {}) }));
  }, []);

  const runRefine = useCallback(
    async (nfcText, refineNeeded) => {
      if (!refineNeeded || refineNeeded.length === 0) return;
      setRefining(new Set(refineNeeded));
      // Progressive updates: refine caches each word the moment its AI call
      // completes, so poll the cache-only peek endpoint and upgrade words
      // one by one instead of waiting for the whole batch.
      pollRef.current = setInterval(async () => {
        try {
          const snap = await peekMorphology(nfcText);
          const done = Object.entries(snap.tokens || {})
            .filter(([k, v]) => v.source === "openai" && refineNeeded.includes(k));
          if (done.length) {
            mergeTokens(Object.fromEntries(done));
            setRefining((prev) => {
              const next = new Set(prev);
              done.forEach(([k]) => next.delete(k));
              return next;
            });
          }
        } catch { /* polling is best-effort */ }
      }, 4000);
      try {
        const data = await refineMorphology(nfcText, refineNeeded);
        mergeTokens(data.tokens);
        setRefineRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
      } catch (err) {
        if (err.status === 403 && err.code === "refine_locked") {
          setUpsell("refine_locked");
          track("irab-refine-locked-view", { context });
        } else if (err.status === 402) {
          mergeTokens(err.tokens); // keep what got refined before credit ran out
          setNotice(err.message || "Your monthly AI credit is used up — showing the instant analysis.");
        } else {
          setNotice("AI refinement didn't finish — showing the instant analysis.");
        }
      } finally {
        clearInterval(pollRef.current);
        setRefining(new Set());
      }
    },
    [mergeTokens, context],
  );

  const analyze = useCallback(
    async (rawText) => {
      const trimmed = (rawText || "").trim();
      if (!trimmed || loading) return;
      setLoading(true);
      setError("");
      setNotice("");
      setUpsell(null);
      setTokens(null);
      setSentence("");
      try {
        const data = await analyzeMorphology(trimmed);
        track("irab-analyze", { context, tier: data.tier || "unknown" });
        setSentence(data.sentence);
        setTokens(data.tokens);
        if (data.tier) setTier(data.tier);
        setRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
        if (!data.engine_available && data.refine_needed.length > 0 && data.refine_allowed) {
          setNotice("Instant analysis is offline — the AI is analyzing every word, this can take a minute.");
        }
        if (data.refine_needed.length > 0) {
          if (data.refine_allowed) {
            runRefine(data.sentence, data.refine_needed);
          } else if (data.tier === "free") {
            setUpsell("refine_locked");
            track("irab-refine-locked-view", { context });
          }
        }
      } catch (err) {
        if (err.status === 429 && err.signupCta) {
          setUpsell("quota");
          setUpsellRetryAt(err.retryAt || null);
        } else {
          setError(err.message || "Something went wrong. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [loading, runRefine, context],
  );

  // View-only path: read whatever the cache already holds for this sentence.
  // Used by the Quranic topic's per-ayah Iʿrāb for non-admins, who may look
  // at an analysis but never start one. Resolves to true when something was
  // found, false when the sentence has never been analyzed.
  const peek = useCallback(
    async (rawText) => {
      const trimmed = (rawText || "").trim();
      if (!trimmed || loading) return false;
      setLoading(true);
      setError("");
      setNotice("");
      setUpsell(null);
      setTokens(null);
      setSentence("");
      try {
        const data = await peekMorphology(trimmed);
        const found = Object.keys(data.tokens || {}).length > 0;
        track("irab-peek", { context, found });
        setSentence(data.sentence);
        setTokens(found ? data.tokens : null);
        return found;
      } catch (err) {
        setError(err.message || "Something went wrong. Please try again.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [loading, context],
  );

  // Staff-only: toggle the reviewed-correct flag on a word's cached
  // analysis (gold-set curation). Patches local state on success so the
  // ✓ chip flips without a re-analyze.
  const verifyToken = useCallback(
    async (tokenKey, next) => {
      const data = await verifyMorphologyToken(sentence, Number(tokenKey), next);
      setTokens((prev) => {
        if (!prev || !prev[tokenKey]) return prev;
        return { ...prev, [tokenKey]: { ...prev[tokenKey], verified: data.verified } };
      });
      return data.verified;
    },
    [sentence],
  );

  const reset = useCallback(() => {
    clearInterval(pollRef.current);
    setSentence("");
    setTokens(null);
    setRefining(new Set());
    setError("");
    setNotice("");
    setUpsell(null);
    setUpsellRetryAt(null);
  }, []);

  return {
    sentence, tokens, refining, loading, error, notice,
    upsell, upsellRetryAt, tier, remaining, refineRemaining,
    maxChars, subscriberDaily, isStaff,
    analyze, peek, reset, verifyToken,
  };
}
